import { useRouter } from 'expo-router';
import type { RefObject } from 'react';

import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { routes } from '@/constants/routes';
import type { Cart } from '@/types/booking';

interface CheckoutFlowRefs {
  confirmBookingBottomSheetRef: RefObject<BottomSheetRef | null>;
  checkoutFormBottomSheetRef: RefObject<BottomSheetRef | null>;
  cashAppBottomSheetRef: RefObject<BottomSheetRef | null>;
  consentBottomSheetRef: RefObject<BottomSheetRef | null>;
  adultConsentBottomSheetRef: RefObject<BottomSheetRef | null>;
  viewOrderBottomSheetRef: RefObject<BottomSheetRef | null>;
}

interface UseCheckoutFlowHandlersParams {
  cart: Cart | null | undefined;
  isAdultsOnly: boolean;
  shopRoute: string;
  barberId: string;
  promoId?: string;
  isCartExpired: boolean;
  resetCheckout: () => void;
  handleConsentContinue: () => unknown;
  refs: CheckoutFlowRefs;
  waitlistSelectedIds: Map<string, string>;
}

/**
 * Manages the checkout popup flow: validates that all appointments have a
 * selected time before any bottom sheet is opened, and orchestrates the
 * progression through adult-consent → confirm-booking → payment consent.
 */
export const useCheckoutFlowHandlers = ({
  cart,
  isAdultsOnly,
  shopRoute,
  barberId,
  promoId,
  isCartExpired,
  resetCheckout,
  handleConsentContinue,
  waitlistSelectedIds,
  refs: {
    confirmBookingBottomSheetRef,
    checkoutFormBottomSheetRef,
    cashAppBottomSheetRef,
    consentBottomSheetRef,
    adultConsentBottomSheetRef,
    viewOrderBottomSheetRef,
  },
}: UseCheckoutFlowHandlersParams) => {
  const router = useRouter();

  /** Returns true (and closes all popups) when any appointment is missing both a time and a waitlist selection. */
  const closeAllIfMissingTime = () => {
    if (
      cart?.items.every(
        (item) => item.dateTime || waitlistSelectedIds.has(item.id),
      )
    )
      return false;
    [
      consentBottomSheetRef,
      confirmBookingBottomSheetRef,
      checkoutFormBottomSheetRef,
      cashAppBottomSheetRef,
      adultConsentBottomSheetRef,
      viewOrderBottomSheetRef,
    ].forEach((ref) => ref.current?.close());
    return true;
  };

  const handleContinueToPayment = () => {
    if (closeAllIfMissingTime()) return;
    if (isAdultsOnly) adultConsentBottomSheetRef.current?.open();
    else confirmBookingBottomSheetRef.current?.open();
  };

  const handleAdultConsentComplete = () => {
    if (closeAllIfMissingTime()) return;
    confirmBookingBottomSheetRef.current?.open();
  };

  const handleResetCheckout = () => {
    resetCheckout();
    if (isCartExpired) {
      router.setParams({ cart: undefined });
      router.replace(routes.chooseService({ shopRoute, barberId, promoId }));
      return;
    }
    if (closeAllIfMissingTime()) return;
    confirmBookingBottomSheetRef.current?.open();
  };

  const handleValidatedConsentContinue = () => {
    if (closeAllIfMissingTime()) return;
    return handleConsentContinue();
  };

  return {
    handleContinueToPayment,
    handleAdultConsentComplete,
    handleResetCheckout,
    handleValidatedConsentContinue,
  };
};
